import React, { useMemo, useState } from 'react';
import { CollectionView } from 'common';
import { Select } from '@src/components/Select';
import { SortBy } from '../consts';
import CreateCollection from '@src/pages/Popups/components/CollectOverlay/components/CreateCollection';
import { CircularProgress } from '@mui/material';
import { ActionButton } from '@src/components/ActionButton';
import { ADALONG_COLORS } from '@src/styles';
import { SearchAndSelectBar } from '@src/components/SearchAndSelectBar';
import { classes, stylesheet } from 'typestyle';
import { OverlayContainer } from '@src/components/OverlayContainer';

interface Props {
  sortBy: SortBy;
  list: CollectionView[];
  setSortBy: (sortBy: SortBy) => void;
  filter: string;
  setFilter: (val: string) => void;
  activeTab: 'collections' | 'admin';
  setActiveTab: (tab: 'collections' | 'admin') => void;
  isAdminUser: boolean;
  isLoading: boolean;
  onCollectionCreated?: (collection: CollectionView) => void;
}

export function CollectionsHeader({
  sortBy,
  list,
  setSortBy,
  filter,
  setFilter,
  activeTab,
  setActiveTab,
  isAdminUser,
  isLoading,
  onCollectionCreated,
}: Props) {
  const sortKeys: SortBy[] = ['title', 'date'];
  const sortByOptions = sortKeys.map((key) => ({
    value: key,
    text: window.T(`create.collections.sortby.${key}`),
  }));
  const [createCollection, setCreateCollection] = useState(false);
  const currentSortLabel = sortByOptions.find((option) => option.value === sortBy)?.text ?? '';
  const sortRenderValue = currentSortLabel ? `Sort by ${currentSortLabel.toLowerCase()}` : 'Sort by';

  const tabConfig = useMemo(
    () => [
      { key: 'collections' as const, label: 'My collections' },
      { key: 'admin' as const, label: 'Admin collections' },
    ],
    []
  );
  const shouldShowTabs = isAdminUser;
  const handleCloseCreate = () => setCreateCollection(false);
  const handleCollectionCreated = (collection: CollectionView) => {
    onCollectionCreated?.(collection);
    setCreateCollection(false);
  };

  return (
    <div className={sheet.header}>
      <div className={sheet.tabsRow}>
        {shouldShowTabs ? (
          <div className={sheet.tabsContainer} role='tablist'>
            {tabConfig
              .filter((tab) => (tab.key === 'admin' ? isAdminUser : true))
              .map((tab) => {
                const isActive = activeTab === tab.key;
                return (
                  <button
                    key={tab.key}
                    type='button'
                    role='tab'
                    aria-selected={isActive}
                    className={classes(sheet.tabButton, isActive && sheet.tabButtonActive)}
                    onClick={() => setActiveTab(tab.key)}
                  >
                    <span className={sheet.tabLabel}>{tab.label}</span>
                    {isActive ? (
                      <span className={sheet.tabCount}>
                        {isLoading ? (
                          <CircularProgress size={12} thickness={5} />
                        ) : (
                          list.length.toLocaleString()
                        )}
                      </span>
                    ) : null}
                  </button>
                );
              })}
          </div>
        ) : (
          <div className={sheet.simpleTitleRow}>
            <span className={sheet.simpleTitle}>My collections</span>
            <span className={sheet.simpleCount}>
              {isLoading ? (
                <CircularProgress size={12} thickness={5} />
              ) : (
                list.length.toLocaleString()
              )}
            </span>
          </div>
        )}
        <ActionButton className={sheet.createButton} onClick={() => setCreateCollection(true)}>
          CREATE
        </ActionButton>
      </div>
      <div className={sheet.searchRow}>
        <div className={sheet.searchWrapper}>
          <SearchAndSelectBar
            className={sheet.searchBar}
            inputValue={filter}
            handlers={{
              onText: (val) => setFilter(val),
              onCloseBar: () => setFilter(''),
            }}
            appearance='dark'
            rightSlot={
              <div className={sheet.sortWrapper}>
                <Select
                  options={sortByOptions}
                  onChange={setSortBy}
                  value={sortBy}
                  variant='searchInline'
                  displayEmpty
                  renderValue={() => sortRenderValue}
                />
              </div>
            }
          />
        </div>
      </div>
      {createCollection ? (
        <OverlayContainer
          frameClasses={[sheet.overlayFrame]}
          onClose={handleCloseCreate}
          style={{ zIndex: 30 }}
        >
          <CreateCollection onClose={handleCloseCreate} onCreated={handleCollectionCreated} />
        </OverlayContainer>
      ) : null}
    </div>
  );
}

const CONTROL_WIDTH = 200;

const sheet = stylesheet({
  header: {
    display: 'flex',
    flexDirection: 'column',
    margin: '10px 15px 0 15px',
    gap: 12,
  },
  tabsRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    flexWrap: 'wrap',
  },
  tabsContainer: {
    display: 'flex',
    alignItems: 'flex-end',
    gap: 4,
    flexWrap: 'wrap',
    borderBottom: '1px solid rgba(255,255,255,0.12)',
  },
  simpleTitleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
  },
  searchRow: {
    display: 'flex',
    flexDirection: 'row',
    gap: 12,
    alignItems: 'stretch',
    flexWrap: 'wrap',
    marginBottom: 10,
    $nest: {
      '@media (max-width: 900px)': {
        flexWrap: 'wrap',
      },
    },
  },
  searchBar: {
    width: '100%',
    borderRadius: '50px !important',
  },
  searchWrapper: {
    flex: '1 1 auto',
    minWidth: 240,
    width: '100%',
  },
  createButton: {
    whiteSpace: 'nowrap',
    flex: `0 0 ${CONTROL_WIDTH}px`,
    width: CONTROL_WIDTH,
    maxWidth: '100%',
    minWidth: CONTROL_WIDTH,
  },
  sortWrapper: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    color: ADALONG_COLORS.SMOOTH_WHITE,
    width: CONTROL_WIDTH,
    justifyContent: 'flex-end',
  },
  tabButton: {
    border: 'none',
    borderBottom: '2px solid transparent',
    borderRadius: '8px 8px 0 0',
    background: 'transparent',
    color: ADALONG_COLORS.DARK_GRAY,
    padding: '10px 18px 8px',
    fontSize: 16,
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    transition: 'color 0.2s ease, border-color 0.2s ease, background-color 0.2s ease',
  },
  tabButtonActive: {
    backgroundColor: 'rgba(255,255,255,0.05)',
    color: ADALONG_COLORS.SMOOTH_WHITE,
    borderBottomColor: ADALONG_COLORS.FLASH_BLUE,
  },
  tabLabel: {
    fontWeight: 600,
    letterSpacing: 0.4,
    fontSize: 16,
  },
  tabCount: {
    fontSize: 14,
    color: ADALONG_COLORS.LIGHT_GRAY,
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
  },
  simpleTitle: {
    fontSize: 16,
    fontWeight: 600,
    letterSpacing: 0.3,
    color: ADALONG_COLORS.SMOOTH_WHITE,
  },
  simpleCount: {
    fontSize: 14,
    color: ADALONG_COLORS.LIGHT_GRAY,
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
  },
  overlayFrame: {
    background: 'transparent',
    padding: 0,
    border: 'none',
    boxShadow: 'none',
    maxWidth: 'unset',
  },
});
